import EyeIcon from '@mui/icons-material/Visibility';
import { Button, Card, CardContent, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment-timezone';
import React, { Suspense } from 'react';
import { Link, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';

import { useGetAgentSeatCountNumberCreditQuery } from '../../api/apigQuery';
import { LabelledOutline } from '../../common/final-form-material-ui';
import { trackAddSubscriptionMinutesSMSReserve } from '../../common/userTracking';
import { Role, user } from '../../recoil/atoms/user';
import { getBusinessProductsPHP } from '../../recoil/selectors/getBusinessProductsPHP';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import ViewBreakdownModal from '../components/modals/ViewBreakdownModal';
import { GenericSkeleton } from '../modules/Skeleton/GenericSkeleton';
import { Invoices } from '../subpages/Invoices';
import { RechargeDetails } from '../subpages/RechargeDetails';
import { RecurringInvoice } from '../subpages/RecurringInvoice';

import { CancellationModal } from './CancellationModal';

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});
const useStyles = makeStyles((theme) => ({
  root: {
    padding: '0 !important',
    marginTop: 0,
    // [theme.breakpoints.up('md')]: {
    //   marginTop: 0,
    // },
  },
  tabs: {
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #515151' : '1px solid #e0e0e0',
    indicator: {
      backgroundColor: '#0098C7',
    },
  },
  tab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&[aria-selected=true]': {
      color: '#0098C7',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  paper: {
    flexGrow: 1,
  },
  rightComponent: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: '10px',
  },
  leftTopComponent: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  subscriptionMetricsContainer: {
    height: 'fit-content',
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    '& [aria-label=outline-body-content]': {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      marginBottom: 0,
    },
    '& .MuiInputLabel-root': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.background.paper
          : '#f7f5f5',
    },
  },
  subscriptionMetricsCard: {
    minWidth: '6rem',
    flex: '1 1 auto',
    margin: theme.spacing(0.25),
  },
  subscriptionMetricsCardContent: {
    padding: theme.spacing(1),
    '&:last-child': {
      paddingBottom: theme.spacing(1), // reset padding to default
    },
  },
  subscriptionMetricsCardTitle: {
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  subscriptionMetricsCardBody: {
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  eyeButton: {
    borderRadius: '50%',
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: -3,
    minWidth: 'auto',
    '& span': {
      padding: 0,
    },
  },
  tabsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cancelButton: {
    border: '1px solid gray',
    height: '32px',
    marginRight: '8px',
  },
}));
export const SubScriptionHeader = ({
  businessData,
  billingCycleTotal,
  minutes,
  reserveBalance,
}) => {
  const classes = useStyles();
  const userAtom = useRecoilValueLoadable(user);

  const [showRecharge, setShowRecharge] = React.useState(false);
  const [breakdownModal, handleBreakdownModal] = React.useState(false);

  const {
    currentData: agentSeatCountNumberCredit,
  } = useGetAgentSeatCountNumberCreditQuery({
    userSettings: {
      userId: userAtom?.contents?.userid,
      userid: userAtom?.contents?.userid,
      businessId: userAtom?.contents?.businessid,
      businessid: userAtom?.contents?.businessid,
      apikey: userAtom?.contents?.apikey,
    },
  });
  const numberDiscountData = React.useMemo(() => {
    return {
      agents: agentSeatCountNumberCredit?.result?.[0]?.seatCreditCount ?? '-',
      totalDiscount: agentSeatCountNumberCredit?.result?.[0]?.seatCreditCount
        ? `$${(
            agentSeatCountNumberCredit.result[0].seatCreditCount * 5
          ).toFixed(2)}`
        : '-',
    };
  }, [agentSeatCountNumberCredit]);
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  return (
    <>
      {showRecharge && (
        <RechargeDetails
          show={showRecharge}
          onHide={() => setShowRecharge(false)}
        />
      )}
      <ViewBreakdownModal
        show={!!breakdownModal}
        handleClose={handleBreakdownModal}
        billingCycleTotal={billingCycleTotal}
      />
      <div
        className={classes.root}
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
          padding: '12px',
        }}
      >
        <div className={classes.leftTopComponent}>
          {(businessData?.contents?.response?.data?.result?.[0]
            ?.discountPercent ||
            isSuperAdminBoolean) && (
            <LabelledOutline
              customPadding="8px"
              className={classes.subscriptionMetricsContainer}
              label={`Discount`}
            >
              <Card className={classes.subscriptionMetricsCard}>
                <Tooltip title="">
                  <CardContent
                    className={classes.subscriptionMetricsCardContent}
                  >
                    <Typography
                      color="textSecondary"
                      className={classes.subscriptionMetricsCardTitle}
                    >
                      Percent
                    </Typography>
                    <Typography className={classes.subscriptionMetricsCardBody}>
                      {(businessData?.contents?.response?.data?.result?.[0]
                        ?.discountPercent ?? 0) + ' %'}
                    </Typography>
                  </CardContent>
                </Tooltip>
              </Card>
              <Card className={classes.subscriptionMetricsCard}>
                <Tooltip title="">
                  <CardContent
                    className={classes.subscriptionMetricsCardContent}
                  >
                    <Typography
                      color="textSecondary"
                      className={classes.subscriptionMetricsCardTitle}
                    >
                      End date
                    </Typography>
                    <Typography className={classes.subscriptionMetricsCardBody}>
                      {businessData?.contents?.response?.data?.result?.[0]?.discountEndDate?.substring?.(
                        0,
                        10
                      ) ?? 'N/A'}
                    </Typography>
                  </CardContent>
                </Tooltip>
              </Card>
            </LabelledOutline>
          )}
          <LabelledOutline
            className={classes.subscriptionMetricsContainer}
            label={`Reserve Balance`}
            customPadding="8px"
          >
            <Card className={classes.subscriptionMetricsCard}>
              <Tooltip title="">
                <CardContent className={classes.subscriptionMetricsCardContent}>
                  <Typography
                    color="textSecondary"
                    className={classes.subscriptionMetricsCardTitle}
                  >
                    Minutes
                  </Typography>
                  <Typography className={classes.subscriptionMetricsCardBody}>
                    {minutes}
                  </Typography>
                </CardContent>
              </Tooltip>
            </Card>
            <Card className={classes.subscriptionMetricsCard}>
              <Tooltip title="">
                <CardContent className={classes.subscriptionMetricsCardContent}>
                  <Typography
                    color="textSecondary"
                    className={classes.subscriptionMetricsCardTitle}
                  >
                    Balance
                  </Typography>
                  <Typography className={classes.subscriptionMetricsCardBody}>
                    {formatter.format(reserveBalance)}
                  </Typography>
                </CardContent>
              </Tooltip>
            </Card>
          </LabelledOutline>
          <LabelledOutline
            className={classes.subscriptionMetricsContainer}
            label={`Billing`}
            customPadding="8px"
          >
            <Card className={classes.subscriptionMetricsCard}>
              <Tooltip title="">
                <CardContent className={classes.subscriptionMetricsCardContent}>
                  <Typography
                    color="textSecondary"
                    className={classes.subscriptionMetricsCardTitle}
                  >
                    Billing Cycle Total
                  </Typography>
                  <Typography className={classes.subscriptionMetricsCardBody}>
                    {formatter.format(billingCycleTotal)}
                    <Tooltip title="View breakdown">
                      <Button
                        className={classes.eyeButton + ' 0-5'}
                        onClick={() => {
                          handleBreakdownModal(true);
                        }}
                      >
                        <EyeIcon color={'primary'} />
                      </Button>
                    </Tooltip>
                  </Typography>
                </CardContent>
              </Tooltip>
            </Card>
          </LabelledOutline>
          <LabelledOutline
            className={classes.subscriptionMetricsContainer}
            label={`Number Discount`}
            customPadding="8px"
          >
            <Card className={classes.subscriptionMetricsCard}>
              <Tooltip title="">
                <CardContent className={classes.subscriptionMetricsCardContent}>
                  <Typography
                    color="textSecondary"
                    className={classes.subscriptionMetricsCardTitle}
                  >
                    Agents
                  </Typography>
                  <Typography className={classes.subscriptionMetricsCardBody}>
                    {numberDiscountData.agents}
                  </Typography>
                </CardContent>
              </Tooltip>
            </Card>
            <Card className={classes.subscriptionMetricsCard}>
              <Tooltip title="">
                <CardContent className={classes.subscriptionMetricsCardContent}>
                  <Typography
                    color="textSecondary"
                    className={classes.subscriptionMetricsCardTitle}
                  >
                    Total Discount
                  </Typography>
                  <Typography className={classes.subscriptionMetricsCardBody}>
                    {numberDiscountData.totalDiscount}
                  </Typography>
                </CardContent>
              </Tooltip>
            </Card>
          </LabelledOutline>
        </div>
        <div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Button
              className={classes.button}
              color="primary"
              variant="contained"
              disableElevation={true}
              // disabled={props.isLoading}
              onClick={() => {
                trackAddSubscriptionMinutesSMSReserve();
                setShowRecharge(true);
              }}
            >
              Minutes/SMS Reserve
            </Button>
            {/* <Button
      className={classes.button + ' mx-5'}
      color="primary"
      variant="contained"
      disableElevation={true}
      // disabled={props.isLoading}
      onClick={() => handleBreakdownModal(true)}
    >
      view breakdown
    </Button> */}
          </div>
        </div>
      </div>
    </>
  );
};
export function BillingTabs({ basePath = '/billing' }) {
  const classes = useStyles();
  const location = useLocation();

  const [showCancellationModal, setShowCancellationModal] = React.useState(
    false
  );
  const userAtom = useRecoilValueLoadable(user);

  const openCancellationModal = (e) => {
    setShowCancellationModal(true);
  };

  const [_, superAdminBusinessStatsLoadable] = makeApiRequestCached(
    {
      method: 'get',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.GET_SUPER_ADMIN_BUSINESS_STATS +
        `/${userAtom.contents?.businessid}`,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const totalProductSpend = React.useMemo(() => {
    if (
      superAdminBusinessStatsLoadable?.state === 'hasValue' &&
      superAdminBusinessStatsLoadable?.contents?.response?.data?.data
    ) {
      const data =
        superAdminBusinessStatsLoadable?.contents?.response?.data?.data ?? {};
      let parsedStats;
      try {
        if (data.JSON) {
          parsedStats = JSON.parse(data.JSON);
        }
      } catch (er) {
        //
      }
      return parsedStats?.totalProductSpend ?? null;
    }
    return null;
  }, [superAdminBusinessStatsLoadable]);

  const pages = {
    subscription: {
      pathname: `${basePath}/subscription`,
    },
    invoices: {
      pathname: `${basePath}/invoices`,
    },
    // billingHelp: {
    //   pathname: `${basePath}/billing-help`,
    // },
  };
  const data = {
    call: methodNameEnum.GET_BUSINESS_DATA,
  };
  const businessData = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify(data),
    })
  );
  let minutes = 0;
  let reserveBalance = 0;

  if (
    businessData.state === 'hasValue' &&
    businessData?.contents?.response?.data
  ) {
    const data = businessData?.contents?.response?.data?.result?.[0];
    minutes = data?.minutesBalance ?? 0;
    reserveBalance = data?.reserveBalance ?? 0;
  }
  let billingCycleTotal = 0;
  const businessProducts = useRecoilValueLoadable(getBusinessProductsPHP);

  if (
    businessProducts.state === 'hasValue' &&
    businessProducts?.contents?.length > 0
  ) {
    billingCycleTotal = businessProducts?.contents.reduce(
      (sum, businessProduct) => {
        const shouldHaveDiscount =
          !!(
            businessData?.contents?.response?.data?.result?.[0]?.discountEndDate?.substring?.(
              0,
              10
            ) >= moment(businessProduct.invoiceduntil).format('YYYY-MM-DD') ||
            !businessData?.contents?.response?.data?.result?.[0]
              ?.discountEndDate
          ) &&
          !!businessProduct.canHaveDiscount &&
          !!businessData?.contents?.response?.data?.result?.[0]
            ?.discountPercent;
        return businessProduct.status === 'active'
          ? sum +
              (shouldHaveDiscount
                ? (100 -
                    (businessData?.contents?.response?.data?.result?.[0]
                      ?.discountPercent ?? 0)) /
                  100
                : 1) *
                (Number(businessProduct.priceperunit.split('$').join('')) *
                  businessProduct.paymentduration)
          : sum + 0;
      },
      0
    );
  }

  const lastInvoiceUntilDatetime = React.useMemo(() => {
    const tz =
      businessData?.contents?.response?.data?.result?.[0]?.timezone_php;
    try {
      if (
        businessData.state === 'hasValue' &&
        businessProducts.state === 'hasValue' &&
        businessProducts?.contents?.length > 0
      ) {
        const businessProductsArr = businessProducts.contents ?? [];
        let datetime = moment
          .tz(new Date().toISOString(), tz)
          .format('MM-DD-YYYY');
        businessProductsArr.forEach((businessProduct) => {
          if (businessProduct?.invoiceduntil > datetime) {
            datetime = businessProduct.invoiceduntil;
          }
        });
        const localDatetime = tz
          ? datetime // NOTE: moment(null) is invalid date
            ? moment.tz(datetime, tz) // if timezone & datetime not null
            : moment.tz(tz) // if timezone not null and datetime null
          : moment(datetime ?? undefined); // if timezone and datetime both null
        return localDatetime.format('MM-DD-YYYY');
      } else {
        return null;
      }
    } catch (e) {
      return null;
    }
  }, [businessProducts, businessData]);

  const displayCancelButton = React.useMemo(() => {
    if (
      userAtom?.contents &&
      businessData?.contents?.response?.data?.result?.[0]
    ) {
      const role = userAtom.contents.user?.role;
      const validRole = role === 'admin' || role === 'manager';
      const validMRR = !!(
        (totalProductSpend === 0 || !!totalProductSpend) &&
        totalProductSpend < 250
      );
      const billingTerm =
        businessData.contents.response.data.result[0].businessBillingTerm;
      const contractTerm =
        businessData.contents.response.data.result[0].businessContractTerm;
      let bidNotOnContract = false;
      try {
        bidNotOnContract = Number(billingTerm) >= Number(contractTerm);
      } catch (e) {
        //
      }
      const shouldDisplayCancelButton =
        validRole && validMRR && bidNotOnContract && lastInvoiceUntilDatetime;
      return shouldDisplayCancelButton || role === 'superadmin';
    }
    return false;
  }, [
    totalProductSpend,
    userAtom?.contents,
    businessData,
    lastInvoiceUntilDatetime,
  ]);

  return (
    <>
      {window.location.pathname === '/billing/subscription' && (
        <SubScriptionHeader
          businessData={businessData}
          billingCycleTotal={billingCycleTotal}
          minutes={minutes}
          reserveBalance={reserveBalance}
        />
      )}
      <Paper className={classes.paper}>
        {location.pathname !== basePath && // on initial load prevent render since basePath tab undefined
          location.pathname !== `${basePath}/` && (
            <div className={classes.tabsContainer}>
              <div />
              <Tabs
                className={classes.tabs}
                value={location.pathname}
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                <Tab
                  className={classes.tab}
                  label="Subscription"
                  component={Link}
                  value={pages.subscription.pathname}
                  to={{
                    pathname: pages.subscription.pathname,
                    search: location.search,
                  }}
                />
                <Tab
                  className={classes.tab}
                  label="Invoices"
                  component={Link}
                  value={pages.invoices.pathname}
                  to={{
                    pathname: pages.invoices.pathname,
                    search: location.search,
                  }}
                />
              </Tabs>
              {location.pathname === pages.subscription.pathname &&
              displayCancelButton ? (
                <Button
                  onClick={openCancellationModal}
                  className={classes.cancelButton}
                >
                  Cancel
                </Button>
              ) : (
                <div />
              )}
            </div>
          )}

        <Suspense fallback={<GenericSkeleton />}>
          <Switch>
            <Redirect
              exact
              from={basePath}
              to={{
                pathname: pages?.subscription?.pathname,
                search: location.search,
              }}
            />
            <Route
              path={pages.subscription.pathname}
              render={(props) => (
                <RecurringInvoice
                  businessData={businessData}
                  businessProducts={businessProducts}
                  billingCycleTotal={billingCycleTotal}
                  {...props}
                />
              )}
            />
            <Route
              path={pages.invoices.pathname}
              render={(props) => <Invoices {...props} />}
            />
            <Redirect
              from="*"
              to={{
                pathname: pages?.subscription?.pathname,
                search: location.search,
              }}
            />
          </Switch>
        </Suspense>
        <CancellationModal
          show={!!showCancellationModal}
          lastInvoiceUntilDatetime={lastInvoiceUntilDatetime}
          id={111}
          onHide={() => setShowCancellationModal(false)}
        />
      </Paper>
    </>
  );
}
